import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { caseStudiesData } from '../data/caseStudies';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiArrowLeft } = FiIcons;

const LinkedClientName = ({ text, clientName, website }) => {
  if (!website || !text.includes(clientName)) {
    return text;
  }

  const [before, ...after] = text.split(clientName);

  return (
    <>
      {before}
      <a
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        className="text-orange-500 underline underline-offset-4 hover:text-orange-400"
      >
        {clientName}
      </a>
      {after.join(clientName)}
    </>
  );
};

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const study = caseStudiesData.find((item) => item.slug === slug);

  if (!study) {
    return <Navigate to="/case-studies" />;
  }

  const content = study[lang] || study.en;

  return (
    <div className="bg-zinc-950 min-h-screen pt-32 pb-24 px-6">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/case-studies"
          className="inline-flex items-center gap-2 text-orange-500 font-bold mb-12 hover:text-orange-400 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          {t.caseStudies.back}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter mb-6">
            {content.title}
          </h1>
          <p className="text-zinc-500 font-bold uppercase tracking-widest text-sm mb-12">
            Client:{' '}
            {study.website ? (
              <a
                href={study.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-orange-500 transition-colors"
              >
                {study.clientName}
              </a>
            ) : (
              study.clientName
            )}{' '}
            / {study.category}
          </p>

          {study.image && (
            <div className="aspect-video rounded-3xl overflow-hidden mb-16 border border-white/10 shadow-2xl">
              <img src={study.image} alt={`${study.clientName} ${content.title}`} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <div className="space-y-4">
              <h2 className="text-orange-500 font-bold uppercase tracking-widest text-xs">
                {t.caseStudies.challenge}
              </h2>
              <p className="text-zinc-300 text-lg leading-relaxed">
                <LinkedClientName
                  text={content.challenge}
                  clientName={study.clientName}
                  website={study.website}
                />
              </p>
            </div>
            <div className="space-y-4">
              <h2 className="text-orange-500 font-bold uppercase tracking-widest text-xs">
                {t.caseStudies.solution}
              </h2>
              <p className="text-zinc-300 text-lg leading-relaxed">
                <LinkedClientName
                  text={content.solution}
                  clientName={study.clientName}
                  website={study.website}
                />
              </p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 bg-zinc-900 border border-orange-500/20 rounded-3xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/5 blur-3xl rounded-full" />
            <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
              <SafeIcon icon={FiCheckCircle} className="text-orange-500" />
              {t.caseStudies.results}
            </h2>
            <ul className="space-y-6">
              {content.results.map((result, index) => (
                <li key={index} className="flex items-start gap-4 text-zinc-300 text-lg">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-3 shrink-0" />
                  <span>
                    <LinkedClientName
                      text={result}
                      clientName={study.clientName}
                      website={study.website}
                    />
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default CaseStudyDetail;